import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { Text } from "@a7y/tailwind-catalyst";
import { useQuery } from "@tanstack/react-query";
import { CommissionStateBadge, formatCents, LoadingPanel, Section, StatCard } from "./shared";
import type { CommissionState, ProgramOverviewResponse } from "./types";

// The BE only tracks outstanding liability for these three commission states.
const LIABILITY_STATES: Array<{ state: CommissionState; key: "pending" | "due" | "paid" }> = [
  { state: "pending", key: "pending" },
  { state: "due", key: "due" },
  { state: "paid", key: "paid" },
];

export function ProgramOverviewPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["affiliate-overview"],
    queryFn: () => api.affiliates.getOverview(),
  });

  if (isLoading) {
    return (
      <div className="px-4 py-0">
        <PageHeader title="Program Overview" subtitle="Affiliate program headline metrics" />
        <LoadingPanel />
      </div>
    );
  }

  const overview: ProgramOverviewResponse | undefined = data;
  const currency = overview?.currency ?? "usd";

  return (
    <div className="px-4 py-0">
      <PageHeader title="Program Overview" subtitle="Affiliate program headline metrics" />

      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label="Total affiliates" value={overview?.totalAffiliates ?? 0} />
        <StatCard label="Active affiliates" value={overview?.activeAffiliates ?? 0} />
        <StatCard label="Pending affiliates" value={overview?.pendingAffiliates ?? 0} />
        <StatCard
          label="Paid to date"
          value={formatCents(overview?.paidToDateCents ?? 0, currency)}
        />
      </div>

      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
        <StatCard label="Clicks" value={overview?.stats.clicks ?? 0} />
        <StatCard label="Leads" value={overview?.stats.leads ?? 0} />
        <StatCard label="Conversions" value={overview?.stats.conversions ?? 0} />
      </div>

      <Section title="Outstanding liability by commission state">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {LIABILITY_STATES.map(({ state, key }) => (
            <div
              key={state}
              className="flex items-center justify-between rounded-lg bg-zinc-50 px-4 py-3 dark:bg-zinc-800/50"
            >
              <CommissionStateBadge state={state} />
              <span className="font-semibold text-zinc-900 dark:text-zinc-100">
                {formatCents(overview?.liabilityByStateCents?.[key] ?? 0, currency)}
              </span>
            </div>
          ))}
        </div>
        {!overview && (
          <Text className="mt-3 text-sm text-zinc-500">No overview data available.</Text>
        )}
      </Section>
    </div>
  );
}
